import type { RoutesData, RouteWithPageMetaData } from '@/router/routerInitPlugin'
import type { Locale } from '@/settings/langs'
import { getVueRouter } from '@/router/routerInstance'
import { routesTranslations } from '@/router/routesTranslations'
import { defaultLocale, supportedLocales } from '@/settings/langs'

const RE_BIND_PARAMS = /:(\w+)(\(([^)]*)\))?/g
const RE_STRIP_CONSTRAINT = /:[^(]+\(|\)/g

export function isSupportedLocale(value: unknown): value is Locale {
  return typeof value === 'string' && (supportedLocales as readonly string[]).includes(value)
}

export function getRouteLocale(route: RouteWithPageMetaData): string {
  return String(route?.meta?.pageData?.locale || route?.params?.locale || defaultLocale)
}

export function bindParams(path: string, params: Record<string, unknown> | undefined): string {
  return path.replace(RE_BIND_PARAMS, (match, name, _constraint, constraintContent) => {
    const value = params?.[name]
    if (value != null)
      return String(value)
    if (name === 'locale' && constraintContent !== undefined)
      return constraintContent
    return match
  })
}

export function getLangRouteData(routeGroupName: string) {
  return Object.entries(routesTranslations[routeGroupName] || {})
    .filter(([locale]) => {
      const supported = isSupportedLocale(locale)
      if (!supported && import.meta.env.DEV) {
        console.warn(`[routes] Locale "${locale}" in routesTranslations["${routeGroupName}"] is not in supportedLocales — skipped.`)
      }
      return supported
    })
}

export function getLocalePath(routeGroupName: string | undefined, variables: Record<string, unknown> | undefined = undefined, forcedLocale: string = ''): string {
  const route = getVueRouter()?.currentRoute?.value
  const locale = forcedLocale || getRouteLocale(route)
  if (routesTranslations?.[routeGroupName || '']?.[locale]) {
    const slug = routesTranslations?.[routeGroupName || '']?.[locale]?.slug || ''
    const path = (locale === defaultLocale ? `/${slug}` : `/${locale}${slug.length ? `/${slug}` : ''}` || '').replace(RE_STRIP_CONSTRAINT, '')
    if (variables) {
      return bindParams(path, variables)
    }
    return path
  }
  return ''
}
export function getPathWithLocale(path: string, forceLocale?: string) {
  const route = getVueRouter()?.currentRoute?.value
  const locale = forceLocale || getRouteLocale(route)
  return `${locale === defaultLocale ? '' : `/${locale}`}${path.length ? `${path}` : ''}`
}
export function getLocaleRouteName(routeGroupName: string | undefined) {
  const route = getVueRouter()?.currentRoute?.value
  const locale = getRouteLocale(route)
  return `${routeGroupName}-${locale}`
}

export function buildHreflangLinks(routerGroup: string, params: Record<string, unknown> = {}): { rel: string, hreflang: string, href: string }[] {
  const host = import.meta.env.VITE_HOST || ''
  const { locale: _locale, ...pathParams } = params
  const links = getLangRouteData(routerGroup).map(([locale, data]) => {
    const path = buildLangRoutePath(locale, data.slug)
    const resolvedPath = bindParams(path, pathParams)
    return { rel: 'alternate', hreflang: locale, href: `${host}${resolvedPath}` }
  })
  const defaultEntry = links.find(l => l.hreflang === defaultLocale)
  if (defaultEntry) {
    links.push({ rel: 'alternate', hreflang: 'x-default', href: defaultEntry.href })
  }
  return links
}

export function buildLangRoutePath(lang: string, slug: string): string {
  return lang === defaultLocale ? `/${slug}` : `/:locale(${lang})${slug.length ? `/${slug}` : ''}`
}

export function buildLangRoute(routeTemplate: RoutesData): RoutesData[] {
  const routeGroupName = String(routeTemplate.name || '')
  return getLangRouteData(routeGroupName).map(([locale, data]) => ({
    ...routeTemplate,
    path: buildLangRoutePath(locale, data.slug),
    name: `${routeGroupName}-${locale}`,
    meta: {
      ...routeTemplate.meta,
      pageData: {
        ...routeTemplate.meta?.pageData,
        title: data.title || '',
        description: data.description || '',
        locale,
        routerGroup: routeGroupName
      }
    }
  }))
}
